import { h, Fragment } from 'preact'
import PropTypes from 'prop-types'
import { useMemo, useState } from 'preact/hooks'

import classNames from 'lib/classNames'
import { useHierarchicalNetworkMembers } from 'lib/hierarchicalNetworkMembersHooks'

import Header from 'components/Header'
import Icon from 'components/Icon'
import Button from 'components/Button'
import Link from 'components/Link'
import Spinner from 'components/Spinner'
import ErrorMessage from 'components/ErrorMessage'
import OrganizationIcon from 'components/OrganizationIcon'
import SingleLine from 'components/SingleLine'
import HierarchicalNetworkGraph from 'components/HierarchicalNetworkGraph'

import './index.sass'

const COMPONENT_NAME = 'HierarchicalNetworkNavigator'

function MemberListItem({ member, organizationApikey }) {
  const isCurrent = member.apikey === organizationApikey
  const href = `/${member.apikey}${member.is_network ? '/network' : ''}`
  const indent = member.depth || 0

  return <div
    className={classNames('HierarchicalNetworkNavigator-listItem', {
      'HierarchicalNetworkNavigator-listItem--master': member.is_master_org,
      'HierarchicalNetworkNavigator-listItem--current': isCurrent,
    })}
    style={{ paddingLeft: `${(indent * 20) + 12}px` }}
  >
    <div className="HierarchicalNetworkNavigator-listItem-connector">
      {indent > 0 && <span className="HierarchicalNetworkNavigator-listItem-line" />}
    </div>
    <Link href={href} className="HierarchicalNetworkNavigator-listItem-content">
      <OrganizationIcon organization={member} size="lg" bordered />
      <div className="HierarchicalNetworkNavigator-listItem-info">
        <div className="HierarchicalNetworkNavigator-listItem-name">
          <SingleLine>{member.name || member.apikey}</SingleLine>
        </div>
        <div className="HierarchicalNetworkNavigator-listItem-role">
          {member.is_master_org ? 'Prime Hub' : 'Hub'}
          {member.depth > 0 && ` · Level ${member.depth}`}
        </div>
        {member.tag_line && (
          <div className="HierarchicalNetworkNavigator-listItem-tagline">
            <SingleLine>{member.tag_line}</SingleLine>
          </div>
        )}
      </div>
      {isCurrent && <span className="HierarchicalNetworkNavigator-listItem-badge">Current</span>}
    </Link>
    <Button type="normal" size="xs" href={href}>Visit</Button>
  </div>
}

export default function HierarchicalNetworkNavigator({
  className,
  organizationApikey,
  ...props
}) {
  const {
    networkType,
    members,
    relationships,
    hierarchicalNetworkMembersLoading: loading,
    hierarchicalNetworkMembersLoadingError: error,
  } = useHierarchicalNetworkMembers(organizationApikey, COMPONENT_NAME)

  const [selectedMember, setSelectedMember] = useState(null)

  const sortedMembers = useMemo(() => {
    if (!members || members.length === 0) return []
    return [...members].sort((a, b) => {
      if (a.is_master_org && !b.is_master_org) return -1
      if (!a.is_master_org && b.is_master_org) return 1
      if (a.depth !== b.depth) return a.depth - b.depth
      return (a.name || '').localeCompare(b.name || '')
    })
  }, [members])

  const childMembers = useMemo(() =>
    sortedMembers.filter(m => !m.is_master_org),
  [sortedMembers]
  )

  const masterMember = useMemo(() =>
    sortedMembers.find(m => m.is_master_org),
  [sortedMembers]
  )

  const onNodeClick = (_, node) => {
    setSelectedMember(node.data.member)
  }

  if (networkType === 'legacy') {
    return <div
      {...props}
      className={classNames('HierarchicalNetworkNavigator', 'HierarchicalNetworkNavigator--legacy', { className })}
    >
      <Header size="md" centered padded>
        This network uses the legacy flat structure.
      </Header>
    </div>
  }

  return <div
    {...props}
    className={classNames('HierarchicalNetworkNavigator', { className })}
  >
    <ErrorMessage {...{error}}/>
    {loading && <Spinner />}

    {!loading && !error && members.length > 0 && (
      <div className="HierarchicalNetworkNavigator-layout">


        {/* RIGHT: Member List */}
        <div className="HierarchicalNetworkNavigator-listPanel">
          <Header size="sm" underlined className="HierarchicalNetworkNavigator-panelHeader">
            <Icon type="list" size="sm" />
            {' '}Network Members ({members.length})
          </Header>

          {masterMember && (
            <Fragment>
              <Header size="xs" italic className="HierarchicalNetworkNavigator-sectionHeader">
                Prime Hub
              </Header>
              <MemberListItem
                member={masterMember}
                organizationApikey={organizationApikey}
              />
            </Fragment>
          )}

          {childMembers.length > 0 && (
            <Fragment>
              <Header size="xs" italic className="HierarchicalNetworkNavigator-sectionHeader">
                Child Hubs ({childMembers.length})
              </Header>
              {childMembers.map(member =>
                <MemberListItem
                  key={member.apikey}
                  member={member}
                  organizationApikey={organizationApikey}
                />
              )}
            </Fragment>
          )}

          {childMembers.length === 0 && (
            <Header size="md" italic centered padded>
              No child hubs in this network
            </Header>
          )}

          {selectedMember && (
            <div className="HierarchicalNetworkNavigator-selectedInfo">
              <Header size="sm" underlined>
                Selected: {selectedMember.name}
              </Header>
              <div className="HierarchicalNetworkNavigator-selectedInfo-details">
                <OrganizationIcon organization={selectedMember} size="lg" bordered />
                <div>
                  <Header size="md">{selectedMember.name}</Header>
                  {selectedMember.tag_line && <p>{selectedMember.tag_line}</p>}
                  <p>{selectedMember.is_master_org ? 'Prime Hub' : `Hub · Level ${selectedMember.depth}`}</p>
                  <Button
                    type="normal"
                    size="sm"
                    href={`/${selectedMember.apikey}${selectedMember.is_network ? '/network' : ''}`}
                  >
                    Visit Hub
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* LEFT: Graph View */}
        <div className="HierarchicalNetworkNavigator-graphPanel">
          <Header size="sm" underlined className="HierarchicalNetworkNavigator-panelHeader">
            <Icon type="treeStructure" size="lg" />
            {' '}Network Hierarchy
          </Header>
          <div className="HierarchicalNetworkNavigator-graphContainer">
            <HierarchicalNetworkGraph
              members={members}
              relationships={relationships}
              onNodeClick={onNodeClick}
            />
          </div>
        </div>
      </div>
    )}

    {!loading && !error && members.length === 0 && (
      <div className="HierarchicalNetworkNavigator-empty">
        <Icon type="network" />
        <Header size="xl" centered>Hierarchical Network</Header>
        <br/>
        <Header size="md" centered>
          No hierarchical network members found for this hub.
        </Header>
      </div>
    )}
  </div>
}

HierarchicalNetworkNavigator.propTypes = {
  className: PropTypes.string,
  organizationApikey: PropTypes.string.isRequired,
}
